import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaBookmark,
  FaCalendarAlt,
  FaDownload,
  FaMapMarkedAlt,
  FaRegTrashAlt,
  FaRoute,
  FaShareAlt,
} from "react-icons/fa";
import NavBar from "./nav";
import Footer from "./footer";
import { createPrintableItinerary, getRouteSegments } from "../utils/itineraryPdf";
import { clearTripItems, getTripItems, removeTripItem } from "../utils/tripPlanner";
import { notifyInfo, notifySuccess } from "../utils/notify";
import "../css/tripPlanner.css";

const categoryOrder = [
  "Accommodation",
  "Buses",
  "Transport",
  "Heritage Landmark",
  "Museum",
  "Arts & Culture",
  "Attractions",
  "Nature Experience",
  "National Park",
  "Restaurants",
  "Food",
  "Shopping Malls",
  "Shopping",
  "Events",
];

const getCategoryRank = (category = "") => {
  const matchIndex = categoryOrder.findIndex((item) =>
    category.toLowerCase().includes(item.toLowerCase())
  );

  return matchIndex === -1 ? categoryOrder.length : matchIndex;
};

const createItineraryText = (items) => {
  const orderedItems = getSuggestedRoute(items);
  const routeSegments = getRouteSegments(orderedItems);

  return [
    "Kim Tour - Saved Trip Itinerary",
    "",
    `Saved stops: ${items.length}`,
    "",
    "Suggested route order:",
    ...routeSegments.map(
      ({ item, distanceKm }, index) =>
        `${index + 1}. ${item.title || item.name} (${item.category || "Saved Idea"})${
          item.location ? ` - ${item.location}` : ""
        }${index > 0 && typeof distanceKm === "number" ? ` - approx. ${distanceKm.toFixed(1)} km from previous stop` : ""}`
    ),
    "",
    "Saved by category:",
    ...items.map(
      (item) =>
        `- ${item.title || item.name} | ${item.category || "Saved Idea"}${
          item.location ? ` | ${item.location}` : ""
        }`
    ),
  ].join("\n");
};

const getSuggestedRoute = (items) =>
  [...items].sort((a, b) => {
    const categoryDifference = getCategoryRank(a.category) - getCategoryRank(b.category);
    if (categoryDifference !== 0) return categoryDifference;
    return (a.title || a.name || "").localeCompare(b.title || b.name || "");
  });

const TripPlanner = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(() => getTripItems());

  const groupedItems = useMemo(
    () =>
      items.reduce((groups, item) => {
        const key = item.category || "Saved Ideas";
        return {
          ...groups,
          [key]: [...(groups[key] || []), item],
        };
      }, {}),
    [items]
  );

  const suggestedRoute = useMemo(() => getSuggestedRoute(items), [items]);
  const routeSegments = useMemo(() => getRouteSegments(suggestedRoute), [suggestedRoute]);
  const itineraryText = useMemo(() => createItineraryText(items), [items]);

  const removeItem = (item) => {
    const nextItems = removeTripItem(item.id);
    setItems(nextItems);
    notifySuccess(`${item.title || item.name} removed from your trip.`);
  };

  const clearPlanner = () => {
    if (!items.length) {
      notifyInfo("Your trip planner is already empty.");
      return;
    }

    clearTripItems();
    setItems([]);
    notifySuccess("Your trip planner has been cleared.");
  };

  const openItem = (item) => {
    if (!item.detailPath) {
      notifyInfo("Details are not available for this saved item yet.");
      return;
    }

    navigate(item.detailPath, { state: item.detailState || {} });
  };

  const shareItinerary = async () => {
    if (!items.length) {
      notifyInfo("Save a few items before sharing your itinerary.");
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Kimberley Trip Itinerary",
          text: itineraryText,
        });
        notifySuccess("Itinerary shared successfully.");
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(itineraryText);
      notifySuccess("Itinerary copied to clipboard.");
    } catch (error) {
      notifyInfo("Sharing is not available on this device. Use Download instead.");
    }
  };

  const downloadItinerary = () => {
    if (!items.length) {
      notifyInfo("Save a few items before downloading your itinerary PDF.");
      return;
    }

    const printableHtml = createPrintableItinerary({
      items,
      orderedItems: suggestedRoute,
    });
    const blob = new Blob([printableHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!printWindow) {
      URL.revokeObjectURL(url);
      notifyInfo("Please allow popups, then try the PDF download again.");
      return;
    }

    setTimeout(() => URL.revokeObjectURL(url), 30000);
    notifySuccess("PDF itinerary opened. Choose Save as PDF in the print window.");
  };

  return (
    <div className="trip-planner-page">
      <NavBar />

      <section className="hero-section trip-planner-hero">
        <div className="hero-content">
          <h1 className="hero-title">My Trip Planner</h1>
          <p className="hero-subtitle">
            Keep your favourite Kimberley stays, food spots, routes, events, and
            attractions in one simple itinerary.
          </p>
        </div>
      </section>

      <main className="trip-planner-shell">
        <section className="trip-planner-summary">
          <div>
            <p className="trip-planner-eyebrow">
              <FaBookmark /> Saved itinerary
            </p>
            <h2>{items.length} saved {items.length === 1 ? "item" : "items"}</h2>
            <p>
              Add places from cards across the site, then return here to review
              and plan your route.
            </p>
          </div>

          <div className="trip-summary-actions">
            <button type="button" onClick={shareItinerary}>
              <FaShareAlt />
              Share
            </button>
            <button type="button" onClick={downloadItinerary}>
              <FaDownload />
              PDF
            </button>
            <button type="button" className="danger" onClick={clearPlanner}>
              <FaRegTrashAlt />
              Clear
            </button>
          </div>
        </section>

        {items.length ? (
          <div className="trip-planner-groups">
            <section className="trip-route-panel">
              <div className="trip-route-heading">
                <span>
                  <FaRoute />
                </span>
                <div>
                  <h3>Estimated Route Order</h3>
                  <p>
                    A simple suggested flow based on category: stay or transport
                    first, then attractions, food, shopping, and events.
                  </p>
                </div>
              </div>

              <div className="trip-route-list">
                {routeSegments.map(({ item, distanceKm }, index) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => openItem(item)}
                  >
                    <span>{index + 1}</span>
                    <div>
                      <strong>{item.title || item.name}</strong>
                      <small>{item.category || "Saved Idea"}</small>
                      {index > 0 && (
                        <em>
                          {typeof distanceKm === "number"
                            ? `Approx. ${distanceKm.toFixed(1)} km from previous stop`
                            : "Distance estimate unavailable"}
                        </em>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {Object.entries(groupedItems).map(([category, categoryItems]) => (
              <section className="trip-planner-group" key={category}>
                <div className="trip-group-heading">
                  <span>
                    <FaMapMarkedAlt />
                  </span>
                  <div>
                    <h3>{category}</h3>
                    <p>
                      {categoryItems.length} saved{" "}
                      {categoryItems.length === 1 ? "stop" : "stops"}
                    </p>
                  </div>
                </div>

                <div className="trip-planner-grid">
                  {categoryItems.map((item) => (
                    <article className="trip-planner-card" key={item.id}>
                      <div className="trip-card-media">
                        {item.image ? (
                          <img src={item.image} alt={item.title || item.name} />
                        ) : (
                          <div>
                            <FaCalendarAlt />
                          </div>
                        )}
                      </div>

                      <div className="trip-card-content">
                        <h4>{item.title || item.name}</h4>
                        <p>{item.description || "Saved Kimberley trip idea."}</p>

                        <div className="trip-card-actions">
                          <button type="button" onClick={() => openItem(item)}>
                            View Details <FaArrowRight />
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => removeItem(item)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <section className="trip-planner-empty">
            <FaBookmark />
            <h2>No saved items yet</h2>
            <p>
              Browse the site and use "Save Trip" on cards to build your Kimberley
              itinerary.
            </p>
            <button type="button" onClick={() => navigate("/")}>
              Start Exploring
            </button>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TripPlanner;
